import React, { useState, useEffect } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Hooks
import { useNavigate } from 'react-router-dom';

// Actions
import { fetchCampaigns } from '~/redux/actions/campaign-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import NoCampaignsScreen from './NoCampaignsScreen';
import CampaignsTable from './Table';
import OlcButton from '~/components/General/OlcButton';
import OlcSearchInput from '~/components/General/OlcSearchInput';
import LimitModal from '../General/LimitModal';

// MUI Components
import { Container, Box, Grid, Typography, useMediaQuery } from '@mui/material';

// Styles
import './styles.scss';

const Campaigns = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [limitModal, setLimitModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const dispatch = useDispatch();

  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);
  const campaigns = useSelector((state) => state.campaignsReducers.campaigns);
  const rowsCount = useSelector((state) => state.campaignsReducers.rowsCount);
  const from = useSelector((state) => state.campaignsReducers.from);
  const to = useSelector((state) => state.campaignsReducers.to);
  const currentPage = useSelector((state) => state.campaignsReducers.currentPage);
  const isLoading = useSelector((state) => state.campaignsReducers.loading);
  const canCreateCampaign = useSelector(
    (state) => state.userReducers?.user?.subscribedPlan?.isDefault === false
  );
  const totalCampaignsInDB = useSelector((state) => state.campaignsReducers.totalRecordsInDB);
  const totalRevenue = useSelector((state) => state.campaignsReducers.totalRevenue);
  const totalQuantity = useSelector((state) => state.campaignsReducers.totalQuantity);
  const perPage = useSelector((state) => state.campaignsReducers.perPage);
  // Use the useMediaQuery hook to check the screen size
  const isXlScreen = useMediaQuery((theme) => theme.breakpoints.up('xl'));
  // Determine the maxWidth based on the screen size
  const maxWidth = isXlScreen ? 'xxl' : 'xl';

  const handleSearch = () => {
    if (search) {
      search.trim();
      setPagination({ ...pagination, page: 0 });
      setSearchApplied(true);
      getCampaigns();
    }
  };

  const searchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const getCampaigns = (campaignId) => {
    let payload = {
      pagination,
      search: campaignId ? campaignId : search.length ? search : null,
    };
    dispatch(fetchCampaigns(payload));
  };

  const handleCreateCampaign = () => {
    if (!canCreateCampaign) {
      setLimitModal(true);
      return;
    }

    navigate('/campaigns/create');
  };

  const renderLimitModal = () => (
    <LimitModal open={limitModal} onClose={() => setLimitModal(false)} resourceType="campaigns" />
  );

  useEffect(() => {
    dispatch(fetchCampaigns({ pagination }));
  }, []);

  useEffect(() => {
    if (!search) {
      setSearchApplied(false);
      setSearch('');
      getCampaigns();
    }
  }, [search]);

  useEffect(() => {
    getCampaigns();
  }, [pagination.page, pagination.pageSize, perPage, currentMode]);

  useEffect(() => {
    if (campaigns.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [campaigns]);

  // No compaigns screen
  const noCampaignsScreen = () => {
    return (
      <>
        <Container maxWidth={maxWidth} className="olcEmptyStatePageContainer">
          <NoCampaignsScreen onCreateCampaign={handleCreateCampaign} />
        </Container>
        {renderLimitModal()}
      </>
    );
  };

  // Show no campaigns screen only when loading is done and no records found
  if (!isLoading && totalCampaignsInDB !== null && totalCampaignsInDB < 1) {
    return noCampaignsScreen();
  }

  return (
    <Container maxWidth={maxWidth} className="campaignsPageContainer olcPrimaryListPage">
      <Grid container className="campaignsToolbarRow">
        <Grid item lg={6} md={8} sm={12} xs={12} className="searchBtnWrapper newSearchBtnWrapper">
          <Box className="searchWrapper" sx={{ height: '40px', width: 'var(--olc-search-width)' }}>
            <OlcSearchInput
              searchApplied={searchApplied}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value.trimStart())}
              onKeyDown={searchKeyDown}
              placeholder={MESSAGES.COMPAIGNS.SEARCH_PLACE_HOLDER}
              title={MESSAGES.COMPAIGNS.SEARCH_PLACE_HOLDER}
              onClear={() => {
                setSearch('');
                setSearchApplied(false);
              }}
            />
          </Box>
        </Grid>
        <Grid
          item
          lg={6}
          md={4}
          sm={12}
          xs={12}
          className="campaignsActionsGrid"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Box className="campaignActionsBtnWrapper">
            <OlcButton variant="primary" onClick={handleCreateCampaign}>
              {MESSAGES.COMPAIGNS.CREATE_CAMPAIGN_BUTTON}
            </OlcButton>
          </Box>
        </Grid>
      </Grid>

      <Box className="campaignsContentCard olcPrimaryListContent">
        <CampaignsTable
          campaigns={campaigns}
          rowsCount={rowsCount}
          from={from}
          to={to}
          currentPage={currentPage}
          isLoading={isLoading}
          totalCampaignsInDB={totalCampaignsInDB}
          totalRevenue={totalRevenue}
          totalQuantity={totalQuantity}
          pagination={pagination}
          perPage={perPage}
          setPagination={setPagination}
          setAnchorElMenu={setAnchorElMenu}
          setSelectedCampaign={setSelectedCampaign}
          anchorElMenu={anchorElMenu}
          setSearch={setSearch}
          getCampaigns={getCampaigns}
          handleChangePage={handleChangePage}
          searchApplied={searchApplied}
        />
      </Box>
      {renderLimitModal()}
    </Container>
  );
};

export default Campaigns;
